import { useState, useCallback, MouseEvent } from 'react';
import Button, { ButtonProps as MuiButtonProps } from '@mui/material/Button';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

export interface TransitionButtonProps extends Omit<MuiButtonProps, 'color'> {
  /** The text content of the button */
  children: React.ReactNode;
  /** The content to transition to after click (e.g., "Text copied!") */
  transitionChildren?: React.ReactNode;
  /** The base color of the button (e.g., 'primary', 'secondary') */
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  /** The color to transition to on click (e.g., 'error.main', '#f50057') */
  transitionColor?: string;
  /** The icon to display initially */
  startIcon?: React.ReactElement;
  /** The icon to transition to after the click */
  endIcon?: React.ReactElement;
  /** The total duration of the transition cycle in milliseconds */
  duration?: number;
  /** Optional click handler */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

/**
 * A Button that transitions color and optionally icons on click, returning to
 * the original state after a specified duration.
 */
export const TransitionButton = ({
  children,
  transitionChildren,
  color = 'primary',
  transitionColor,
  startIcon: InitialIcon,
  endIcon: TransitionIcon,
  duration = 1500,
  onClick,
  ...rest
}: TransitionButtonProps) => {
  const theme = useTheme();
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Determine the default color based on the theme and `color` prop
  const defaultBgColor =
    theme.palette[color]?.main || theme.palette['primary']?.main;

  // Determine the transition-to color (defaults to success if not provided)
  const finalTransitionColor =
    theme.palette[transitionColor]?.main || theme.palette['success']?.main;

  // Handles the click and the transition logic
  const handleClick = useCallback(() => {
    setIsTransitioning(true);

    const timer = setTimeout(() => {
      setIsTransitioning(false);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration]);

  const combinedOnClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      handleClick();
      if (onClick) {
        onClick(e as unknown as React.MouseEvent<HTMLButtonElement>);
      }
    },
    [handleClick, onClick]
  );

  // Determine the current background color based on state
  const currentBgColor = isTransitioning
    ? finalTransitionColor
    : defaultBgColor;

  // Determine the current icon to display
  // If no transition icon is provided, keep the initial icon throughout
  const currentIcon =
    isTransitioning && TransitionIcon ? TransitionIcon : InitialIcon;

  // Determine if we are showing the initial icon or a transition icon
  const isInitialIcon = !TransitionIcon || currentIcon === InitialIcon;

  // Icon transition is quicker for snappier feel
  const iconTransitionDuration = `${duration / 3}ms`;

  // Text transition is slightly slower to stay in sync with background
  const textTransitionDuration = `${duration / 3.75}ms`;

  // Text transitions with delayed visibility
  const textTransitions = `opacity ${textTransitionDuration} ease-in-out, visibility 0s linear ${
    isTransitioning ? '0s' : textTransitionDuration
  }`;

  return (
    <Button
      variant="contained"
      onClick={combinedOnClick}
      startIcon={
        (InitialIcon || TransitionIcon) &&
        (TransitionIcon ? (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              margin: 0,
              padding: 0,
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: 0,
                padding: 0,
                transition: `opacity ${iconTransitionDuration} ease-in-out`,
                opacity: isInitialIcon ? 1 : 0,
                visibility: isInitialIcon ? 'visible' : 'hidden',
              }}
            >
              {InitialIcon}
            </Box>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'absolute',
                left: 0,
                top: 0,
                margin: 0,
                padding: 0,
                transition: `opacity ${iconTransitionDuration} ease-in-out`,
                opacity: isInitialIcon ? 0 : 1,
              }}
            >
              {TransitionIcon}
            </Box>
          </Box>
        ) : (
          InitialIcon
        ))
      }
      sx={{
        backgroundColor: `${currentBgColor} !important`,
        '&:hover': {
          backgroundColor: `${
            isTransitioning
              ? finalTransitionColor
              : theme.palette[color]?.dark || theme.palette.action.hover
          } !important`,
        },
        transition: `background-color ${duration / 2}ms ease-in-out`,
      }}
      {...rest}
    >
      {transitionChildren ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gridTemplateRows: '1fr',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              gridColumn: 1,
              gridRow: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: textTransitions,
              opacity: isTransitioning ? 0 : 1,
              whiteSpace: 'nowrap',
              visibility: isTransitioning ? 'hidden' : 'visible',
            }}
          >
            {children}
          </Box>
          <Box
            sx={{
              gridColumn: 1,
              gridRow: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: textTransitions,
              opacity: isTransitioning ? 1 : 0,
              whiteSpace: 'nowrap',
              visibility: isTransitioning ? 'visible' : 'hidden',
            }}
          >
            {transitionChildren}
          </Box>
        </Box>
      ) : (
        children
      )}
    </Button>
  );
};
